import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiErrorDetail } from "@/api";
import { AppShell } from "@/components/AppShell";
import { FabricImage } from "@/components/FabricImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, CameraOff, Search } from "lucide-react";

const Row = ({ label, value, tid }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-zinc-100 py-2">
    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</span>
    <span className="mono text-sm font-semibold" data-testid={tid}>{value}</span>
  </div>
);

export default function ScanThan() {
  const [profile, setProfile] = useState(null);
  const [shade, setShade] = useState(null);
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    return () => { scannerRef.current?.stop().catch(() => {}); };
  }, []);

  const lookup = async (code) => {
    try {
      const { data } = await api.get(`/scan/${encodeURIComponent(code)}`);
      setShade(data);
      toast.success(`${data.quality_name} · shade ${data.shade_number || "—"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const start = async () => {
    setScanning(true);
    try {
      const scanner = new Html5Qrcode("qr-reader", { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (text) => {
          await scanner.stop().catch(() => {});
          setScanning(false);
          lookup(text);
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      toast.error("Could not open the camera — allow camera access, or type the code below");
    }
  };

  const stop = async () => {
    await scannerRef.current?.stop().catch(() => {});
    setScanning(false);
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto w-full max-w-lg px-4 py-6" data-testid="scan-page">
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Scan a than</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Point the camera at a shade QR label to see item, shade, company, stock and sale rate.
        </p>

        <div id="qr-reader" data-testid="scan-reader"
          className={`mt-4 overflow-hidden border border-zinc-200 ${scanning ? "block" : "hidden"}`} />

        <div className="mt-4 flex gap-2">
          {scanning ? (
            <Button variant="outline" className="flex-1 rounded-sm" data-testid="scan-stop-btn" onClick={stop}>
              <CameraOff size={15} className="mr-1.5" /> Stop camera
            </Button>
          ) : (
            <Button className="h-12 flex-1 rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              data-testid="scan-start-btn" onClick={start}>
              <Camera size={17} className="mr-2" /> Scan QR label
            </Button>
          )}
        </div>

        <form
          className="mt-4 flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (manual.trim()) lookup(manual.trim()); }}
        >
          <Input data-testid="scan-manual-input" className="h-11 rounded-sm" value={manual}
            onChange={(e) => setManual(e.target.value)} placeholder="Or type the code under the QR" />
          <Button type="submit" variant="outline" className="h-11 shrink-0 rounded-sm" data-testid="scan-manual-btn">
            <Search size={15} />
          </Button>
        </form>

        {shade && (
          <div className="mt-6 border border-zinc-200 p-4" data-testid="scan-result">
            <div className="flex gap-4">
              {shade.image_path && (
                <FabricImage path={shade.image_path} alt={shade.quality_name}
                  className="h-24 w-24 shrink-0 border border-zinc-200 object-cover" />
              )}
              <div className="min-w-0">
                <h2 className="font-head truncate text-xl font-extrabold tracking-tight"
                  data-testid="scan-item-name">{shade.quality_name}</h2>
                <p className="text-sm text-zinc-500">
                  Shade {shade.shade_number || "—"} · {shade.company_name || "no company"}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <Row label="Quality" value={shade.quality || "—"} tid="scan-quality" />
              <Row label="Centre" value={shade.centre} tid="scan-centre" />
              <Row label="Than stock" value={shade.than_stock} tid="scan-than" />
              <Row label="Meter stock" value={shade.meter_stock} tid="scan-meter" />
              <Row label="Minimum" value={shade.min_stock || "—"} tid="scan-min" />
              <Row label="Sale rate ₹" value={shade.sale_rate ?? "—"} tid="scan-rate" />
            </div>
            {shade.min_stock > 0 && shade.than_stock < shade.min_stock && (
              <p className="mt-3 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700" data-testid="scan-low-warning">
                Below minimum — needs reordering
              </p>
            )}
          </div>
        )}
      </main>
    </AppShell>
  );
}
